/**
 * Batch crop geometry. A crop is a normalized rect in 0..1 of the source image —
 * the same `{x, y, width, height}` shape as the editor's `Rectangle`, so the two
 * are interchangeable data.
 *
 * Interaction math runs in frame pixels (the on-screen box the source is
 * contain-fitted into). The frame has the source's aspect ratio, so a ratio lock
 * in frame pixels is a ratio lock in source pixels; a 1:1 crop is not a 1:1
 * normalized rect on a non-square image.
 */

import type { CropAspectRatioId } from '@/scenes/Editor/components/Toolbox/ToolboxItems/components/CropRatioOptions'
import { type BatchFlip, flipNormalizedBox } from './flip'

export interface CropRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * A saved Batch crop: a frame over the untouched source, never baked into it.
 * `rect` is in the space the crop stage draws over — the photo after its flip
 * (`BatchFlip`, a separate photo edit), so the flip applies first. `ratioId` is
 * the preset the user last chose, restored when Crop reopens.
 *
 * This file is both the crop's definition (this type) and its geometry, the
 * way `positionFit.ts` holds `PositionConfig` and its math.
 */
export interface BatchCrop {
  rect: CropRect
  ratioId: CropAspectRatioId
}

export type CropResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

export const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 }

/** Smallest crop side, in frame pixels. */
export const MIN_CROP_SIZE_PX = 48

interface PixelRect {
  left: number
  top: number
  right: number
  bottom: number
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

function toPixels(rect: CropRect, frameWidth: number, frameHeight: number): PixelRect {
  return {
    left: rect.x * frameWidth,
    top: rect.y * frameHeight,
    right: (rect.x + rect.width) * frameWidth,
    bottom: (rect.y + rect.height) * frameHeight,
  }
}

function fromPixels(px: PixelRect, frameWidth: number, frameHeight: number): CropRect {
  const left = clamp(px.left, 0, frameWidth)
  const top = clamp(px.top, 0, frameHeight)
  const right = clamp(px.right, left, frameWidth)
  const bottom = clamp(px.bottom, top, frameHeight)
  return {
    x: left / frameWidth,
    y: top / frameHeight,
    width: (right - left) / frameWidth,
    height: (bottom - top) / frameHeight,
  }
}

/** The smallest side a crop may have on this frame — never more than the frame itself. */
function minSide(frameWidth: number, frameHeight: number, minSizePx: number): number {
  return Math.min(minSizePx, frameWidth, frameHeight)
}

export function cropRectsEqual(a: CropRect, b: CropRect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height
}

/** Pixel box of a crop on a frame, for positioning the overlay. */
export function cropRectToPixels(rect: CropRect, frameWidth: number, frameHeight: number) {
  return {
    left: rect.x * frameWidth,
    top: rect.y * frameHeight,
    width: rect.width * frameWidth,
    height: rect.height * frameHeight,
  }
}

/** Moves the crop by a pixel delta, clamped inside the frame. Never resizes. */
export function moveCropRect(
  rect: CropRect,
  dxPx: number,
  dyPx: number,
  frameWidth: number,
  frameHeight: number,
): CropRect {
  if (frameWidth <= 0 || frameHeight <= 0) return rect
  const x = clamp(rect.x + dxPx / frameWidth, 0, 1 - rect.width)
  const y = clamp(rect.y + dyPx / frameHeight, 0, 1 - rect.height)
  return { ...rect, x, y }
}

interface ResizeCropRectParams {
  /** The rect at pointer-down. Deltas are measured from it, not accumulated. */
  rect: CropRect
  handle: CropResizeHandle
  dxPx: number
  dyPx: number
  /** width / height, or null for freeform. */
  aspectRatio: number | null
  frameWidth: number
  frameHeight: number
  minSizePx?: number
}

/**
 * Resizes from a handle, keeping the opposite side (edge) or corner fixed.
 * With a ratio lock, edges grow the other axis symmetrically about the rect's
 * centre line and corners follow the dominant drag axis. Containment scales the
 * locked size down rather than clamping each axis, which would break the ratio.
 */
export function resizeCropRect({
  rect,
  handle,
  dxPx,
  dyPx,
  aspectRatio,
  frameWidth,
  frameHeight,
  minSizePx = MIN_CROP_SIZE_PX,
}: ResizeCropRectParams): CropRect {
  if (frameWidth <= 0 || frameHeight <= 0) return rect
  const start = toPixels(rect, frameWidth, frameHeight)
  const min = minSide(frameWidth, frameHeight, minSizePx)
  const movesWest = handle.includes('w')
  const movesEast = handle.includes('e')
  const movesNorth = handle.includes('n')
  const movesSouth = handle.includes('s')

  if (!aspectRatio) {
    const next = { ...start }
    if (movesWest) next.left = clamp(start.left + dxPx, 0, start.right - min)
    if (movesEast) next.right = clamp(start.right + dxPx, start.left + min, frameWidth)
    if (movesNorth) next.top = clamp(start.top + dyPx, 0, start.bottom - min)
    if (movesSouth) next.bottom = clamp(start.bottom + dyPx, start.top + min, frameHeight)
    return fromPixels(next, frameWidth, frameHeight)
  }

  const ratio = aspectRatio
  const startWidth = start.right - start.left
  const startHeight = start.bottom - start.top
  const minWidth = Math.max(min, min * ratio)

  // Edge handles: drive one axis, centre the other on the start rect.
  if (!(movesWest || movesEast) || !(movesNorth || movesSouth)) {
    if (movesWest || movesEast) {
      const centerY = (start.top + start.bottom) / 2
      const proposed = movesEast ? startWidth + dxPx : startWidth - dxPx
      const availableWidth = movesEast ? frameWidth - start.left : start.right
      const maxWidth = Math.min(availableWidth, 2 * Math.min(centerY, frameHeight - centerY) * ratio)
      const width = clamp(proposed, Math.min(minWidth, maxWidth), maxWidth)
      const height = width / ratio
      const left = movesEast ? start.left : start.right - width
      return fromPixels(
        { left, right: left + width, top: centerY - height / 2, bottom: centerY + height / 2 },
        frameWidth,
        frameHeight,
      )
    }
    const centerX = (start.left + start.right) / 2
    const proposed = movesSouth ? startHeight + dyPx : startHeight - dyPx
    const availableHeight = movesSouth ? frameHeight - start.top : start.bottom
    const maxHeight = Math.min(availableHeight, (2 * Math.min(centerX, frameWidth - centerX)) / ratio)
    const height = clamp(proposed, Math.min(minWidth / ratio, maxHeight), maxHeight)
    const width = height * ratio
    const top = movesSouth ? start.top : start.bottom - height
    return fromPixels(
      { left: centerX - width / 2, right: centerX + width / 2, top, bottom: top + height },
      frameWidth,
      frameHeight,
    )
  }

  // Corner handles: the opposite corner is the anchor.
  const anchorX = movesEast ? start.left : start.right
  const anchorY = movesSouth ? start.top : start.bottom
  const proposedWidth = movesEast ? startWidth + dxPx : startWidth - dxPx
  const proposedHeight = movesSouth ? startHeight + dyPx : startHeight - dyPx
  const drivenWidth = Math.abs(dxPx) >= Math.abs(dyPx) ? proposedWidth : proposedHeight * ratio
  const availableWidth = movesEast ? frameWidth - anchorX : anchorX
  const availableHeight = movesSouth ? frameHeight - anchorY : anchorY
  const maxWidth = Math.min(availableWidth, availableHeight * ratio)
  const width = clamp(drivenWidth, Math.min(minWidth, maxWidth), maxWidth)
  const height = width / ratio
  const left = movesEast ? anchorX : anchorX - width
  const top = movesSouth ? anchorY : anchorY - height
  return fromPixels({ left, top, right: left + width, bottom: top + height }, frameWidth, frameHeight)
}

/**
 * Fits the largest rect of `aspectRatio` inside the current crop, centred on it.
 * Freeform (null) leaves the crop unchanged.
 */
export function constrainCropToAspect(
  rect: CropRect,
  aspectRatio: number | null,
  frameWidth: number,
  frameHeight: number,
): CropRect {
  if (!aspectRatio || frameWidth <= 0 || frameHeight <= 0) return rect
  const px = toPixels(rect, frameWidth, frameHeight)
  const currentWidth = px.right - px.left
  const currentHeight = px.bottom - px.top
  const width = Math.min(currentWidth, currentHeight * aspectRatio)
  const height = width / aspectRatio
  const centerX = (px.left + px.right) / 2
  const centerY = (px.top + px.bottom) / 2
  return fromPixels(
    { left: centerX - width / 2, right: centerX + width / 2, top: centerY - height / 2, bottom: centerY + height / 2 },
    frameWidth,
    frameHeight,
  )
}

/**
 * Re-shapes a crop to `aspectRatio` keeping its area and centre, so switching
 * ratios keeps the crop's relative size instead of nesting each ratio inside
 * the last. Scales down (keeping the ratio) and shifts only as needed to stay
 * inside the frame. Freeform (null) leaves the crop unchanged.
 */
export function fitCropToAspect(
  rect: CropRect,
  aspectRatio: number | null,
  frameWidth: number,
  frameHeight: number,
): CropRect {
  if (!aspectRatio || frameWidth <= 0 || frameHeight <= 0) return rect
  const px = toPixels(rect, frameWidth, frameHeight)
  const area = (px.right - px.left) * (px.bottom - px.top)
  let width = Math.sqrt(area * aspectRatio)
  let height = width / aspectRatio
  const fit = Math.min(1, frameWidth / width, frameHeight / height)
  width *= fit
  height *= fit
  const centerX = clamp((px.left + px.right) / 2, width / 2, frameWidth - width / 2)
  const centerY = clamp((px.top + px.bottom) / 2, height / 2, frameHeight - height / 2)
  return fromPixels(
    { left: centerX - width / 2, right: centerX + width / 2, top: centerY - height / 2, bottom: centerY + height / 2 },
    frameWidth,
    frameHeight,
  )
}

/** The crop an uncropped photo has; stored as null. */
export const DEFAULT_BATCH_CROP: BatchCrop = { rect: FULL_CROP, ratioId: 'freeform' }

/** A crop that keeps the whole photo, whatever the ratio preset. */
export function isIdentityCrop(crop: BatchCrop | null | undefined): boolean {
  return !crop || cropRectsEqual(crop.rect, FULL_CROP)
}

/** Same saved crop; null is the default. */
export function batchCropsEqual(a: BatchCrop | null | undefined, b: BatchCrop | null | undefined): boolean {
  const left = a ?? DEFAULT_BATCH_CROP
  const right = b ?? DEFAULT_BATCH_CROP
  return left.ratioId === right.ratioId && cropRectsEqual(left.rect, right.rect)
}

/** Aspect (width / height) of the cropped region of a source with `sourceAspectRatio`. */
export function getCroppedAspectRatio(sourceAspectRatio: number, rect: CropRect): number {
  if (rect.width <= 0 || rect.height <= 0) return sourceAspectRatio
  return (sourceAspectRatio * rect.width) / rect.height
}

/** Pixel size of the cropped region of a `width` × `height` source, at least 1×1. */
export function getCroppedSize(width: number, height: number, rect: CropRect): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(width * rect.width)),
    height: Math.max(1, Math.round(height * rect.height)),
  }
}

/**
 * Re-expresses a subject box (0..1 of the unflipped source) in what the photo
 * shows: flipped, then clipped to `rect` and normalized to it. Null when the
 * subject lies entirely outside the crop.
 */
export function cropNormalizedBox(box: CropRect, rect: CropRect, flip?: BatchFlip | null): CropRect | null {
  if (rect.width <= 0 || rect.height <= 0) return null
  const flipped = flipNormalizedBox(box, flip)
  const left = Math.max(flipped.x, rect.x)
  const top = Math.max(flipped.y, rect.y)
  const right = Math.min(flipped.x + box.width, rect.x + rect.width)
  const bottom = Math.min(flipped.y + box.height, rect.y + rect.height)
  if (right <= left || bottom <= top) return null
  return {
    x: (left - rect.x) / rect.width,
    y: (top - rect.y) / rect.height,
    width: (right - left) / rect.width,
    height: (bottom - top) / rect.height,
  }
}

/** A box in percent of its container. */
export interface PercentBox {
  left: number
  top: number
  width: number
  height: number
}

/**
 * The cropped region of `contentAspectRatio`, fitted (`contain`, letterboxed)
 * or filled (`cover`) into a container of `containerAspectRatio`, centred, as
 * percentages of the container. Never stretched.
 */
export function fitBoxInContainer(
  contentAspectRatio: number,
  containerAspectRatio: number,
  fit: 'contain' | 'cover',
): PercentBox {
  if (!(contentAspectRatio > 0) || !(containerAspectRatio > 0)) return { left: 0, top: 0, width: 100, height: 100 }
  const relative = contentAspectRatio / containerAspectRatio
  const widthBinds = fit === 'contain' ? relative >= 1 : relative < 1
  const width = widthBinds ? 100 : 100 * relative
  const height = widthBinds ? 100 / relative : 100
  return { left: (100 - width) / 2, top: (100 - height) / 2, width, height }
}

/**
 * Where the whole (flipped) photo sits, in percent of a window that shows
 * exactly `rect` of it: blown up to 1/w × 1/h and shifted so the crop shows
 * through. Flip the photo in place (`scale(-1)` about its centre) to match.
 */
export function getCropWindowImageBox(rect: CropRect): PercentBox {
  return {
    left: (-rect.x * 100) / rect.width,
    top: (-rect.y * 100) / rect.height,
    width: 100 / rect.width,
    height: 100 / rect.height,
  }
}

/**
 * Where a frame showing the whole photo must be drawn so that its `rect`
 * region lands on `target` — the box the cropped region is shown in.
 */
export function expandCropTarget(
  target: { left: number; top: number; width: number; height: number },
  rect: CropRect,
): { left: number; top: number; width: number; height: number } {
  const width = target.width / rect.width
  const height = target.height / rect.height
  return { left: target.left - rect.x * width, top: target.top - rect.y * height, width, height }
}
